import React from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Minus, Package, Plus } from "lucide-react";
import PopUp from "../../containers/PopUp";
import { upsertCartItem } from "../../../server/CollectSite";
import { guardCustomerAction } from "../../../utils/emailVerification";
import { getMaxStockQty, notifyStockLimit } from "../../../utils/stockLimit";
import { getPackageQuantity, getPromoId } from "../sales/promotionUtils";
import { formatPrice } from "../../../utils/formatPrice";
import { getProductHeaderMetaParts } from "../../../utils/formatProductPackSize";
import { totalQuantityAfterSettingBundles } from "../../../utils/packageCartLines";
import "./PackagePromoPopUp.scss";

/**
 * Customer counts packages here. Individual units stay on a separate cart line;
 * changing the package count never wipes leftover units.
 */
const PackagePromoPopUp = ({ product, promo, close }) => {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const inCart = useSelector((state) =>
    state.productsInCart.productsInCart.find(
      (item) => item.productId === product?.productId,
    ),
  );

  const packQuantity = getPackageQuantity(promo);
  if (!product || packQuantity < 1) return null;

  const productHeaderMeta = getProductHeaderMetaParts(product).join(" | ");

  const packagePrice = Number(promo?.discountValue) || 0;
  const unitPrice = packagePrice / packQuantity;
  const originalUnitPrice =
    product?.pricing?.price != null ? Number(product.pricing.price) : null;
  const showOriginalStrike =
    originalUnitPrice != null &&
    Number.isFinite(originalUnitPrice) &&
    originalUnitPrice > unitPrice + 0.001;

  const unitsInCart = inCart?.quantity || 0;
  // Derived from the cart line rather than kept in state, so a rejected update
  // that rolls back in the reducer also rolls back the count shown here.
  const packagesInCart = Math.floor(unitsInCart / packQuantity);
  const leftoverUnits = unitsInCart % packQuantity;
  const total = packagesInCart * packagePrice;

  const availableUnits = product?.availability?.["availableQuantity"];
  const maxUnits = getMaxStockQty(
    availableUnits,
    packQuantity,
    product?.allowOrderWhenOos,
  );
  const canAddPackage =
    (packagesInCart + 1) * packQuantity + leftoverUnits <= maxUnits;
  // Stock too small for even one package - the plus can never open up here.
  const stockCoversPackage = maxUnits >= packQuantity;

  const addBlockedReason = canAddPackage
    ? undefined
    : stockCoversPackage
      ? "הגעת למגבלת המלאי"
      : `אין מספיק מלאי למארז שלם (${Number(availableUnits) || 0} יח' במלאי)`;

  const applyPackages = (packages) => {
    if (guardCustomerAction(dispatch, { user: userState })) return;

    dispatch(
      upsertCartItem(
        product.productId,
        unitPrice,
        totalQuantityAfterSettingBundles(
          { quantity: inCart?.quantity || 0, product },
          packages,
        ),
        String(userState.stationId),
        getPromoId(promo),
        inCart?.product || product,
      ),
    );
  };

  const handleIncrement = () => {
    if (!canAddPackage) {
      notifyStockLimit(maxUnits);
      return;
    }
    applyPackages(packagesInCart + 1);
  };

  const handleDecrement = () => {
    if (packagesInCart <= 0) return;
    applyPackages(packagesInCart - 1);
  };

  if (!product || packQuantity < 1) return null;

  return ReactDOM.createPortal(
    <PopUp width="420px" close={close}>
      <div className="package-promo">
        <h3 className="package-promo__title">
          <Package size={20} strokeWidth={2.2} aria-hidden />
          רכישה במארז
        </h3>
        <p className="package-promo__product">
          {productHeaderMeta || product.name}
        </p>

        <dl className="package-promo__details">
          <div className="package-promo__row package-promo__row--unit-price">
            <dt>מחיר ליחידה</dt>
            <dd className="package-promo__unit-prices">
              {showOriginalStrike ? (
                <span className="package-promo__original-price">
                  {formatPrice(originalUnitPrice)} ₪
                </span>
              ) : null}
              <span className="package-promo__sale-price">
                {formatPrice(unitPrice)} ₪
              </span>
            </dd>
          </div>
          <div className="package-promo__row">
            <dt>כמות במארז</dt>
            <dd>{packQuantity} יח'</dd>
          </div>
          <div className="package-promo__row">
            <dt>מחיר מארז</dt>
            <dd>{formatPrice(packagePrice)} ₪</dd>
          </div>
        </dl>

        <div className="package-promo__counter">
          <button
            type="button"
            className="package-promo__step"
            onClick={handleIncrement}
            disabled={!canAddPackage}
            title={addBlockedReason}
            aria-label="הוספת מארז"
          >
            <Plus size={18} strokeWidth={2.4} aria-hidden />
          </button>
          <span className="package-promo__count">{packagesInCart}</span>
          <button
            type="button"
            className="package-promo__step"
            onClick={handleDecrement}
            disabled={packagesInCart <= 0}
            title={packagesInCart <= 0 ? "אין מארזים בסל" : undefined}
            aria-label="הסרת מארז"
          >
            <Minus size={18} strokeWidth={2.4} aria-hidden />
          </button>
        </div>

        {addBlockedReason ? (
          <p className="package-promo__notice">{addBlockedReason}</p>
        ) : null}

        <div className="package-promo__summary">
          <span>
            סה״כ {packagesInCart} מארזים ({packagesInCart * packQuantity} יח')
          </span>
          <strong>{formatPrice(total)} ₪</strong>
        </div>

        <button type="button" className="ui-btn-brand" onClick={close}>
          סיום
        </button>
      </div>
    </PopUp>,
    document.body,
  );
};

export default PackagePromoPopUp;
